import { createApiRef } from '@backstage/core-plugin-api';

export interface BuildInfo {
  id: number;
  number: string;
  url: string;
  finishedAt?: string;
  branch?: string;
  commit?: string;
}

export interface PackageInfo {
  name: string;
  version: string;
  purl?: string;
  shipped?: boolean;
}

export interface CompositionSummary {
  product: string;
  build: BuildInfo;
  version?: string;
  compositionId?: string;
  packageCount: number;
  error?: string;
}

export interface CompositionRecord
  extends Omit<CompositionSummary, 'packageCount'> {
  packages: PackageInfo[];
}

export interface PackageChanges {
  added: PackageInfo[];
  removed: PackageInfo[];
  updated: { name: string; from: string; to: string }[];
}

export interface CompositionGroup {
  compositionId: string;
  versions: string[];
  builds: BuildInfo[];
  firstSeen?: string;
  lastSeen?: string;
  packageCount: number;
  changes?: PackageChanges;
}

export interface ProductSummary {
  name: string;
  project: string;
  pipelineId: number;
  pipelineUrl: string;
  branch?: string;
  source: 'config' | 'ui';
  addedBy?: string;
  latest?: CompositionSummary;
  currentSince?: string;
  compositionCount?: number;
  error?: string;
}

export interface ProductHistory extends ProductSummary {
  builds: CompositionSummary[];
  compositions: CompositionGroup[];
}

export interface CompositionLookup {
  compositionId: string;
  builds: CompositionSummary[];
  packages: PackageInfo[];
  products: { name: string; current: boolean; latestVersion?: string }[];
}

export interface PipelineCheck {
  project: string;
  pipelineId: number;
  pipelineName: string;
  pipelineUrl: string;
  artifacts: string[];
  latestBuild?: BuildInfo;
}

export interface NewProduct {
  name: string;
  project: string;
  pipelineId: number;
  artifact: string;
  branch?: string;
}

export interface CompositionsApi {
  listProducts(): Promise<ProductSummary[]>;
  getProduct(name: string): Promise<ProductHistory>;
  getBuild(product: string, buildId: number): Promise<CompositionRecord>;
  findComposition(compositionId: string): Promise<CompositionLookup>;
  checkPipeline(url: string, branch?: string): Promise<PipelineCheck>;
  addProduct(product: NewProduct): Promise<void>;
  removeProduct(name: string): Promise<void>;
  downloadSbom(
    product: string,
    buildId: number,
  ): Promise<{ blob: Blob; fileName: string }>;
}

export const compositionsApiRef = createApiRef<CompositionsApi>({
  id: 'plugin.compositions.service',
});
